/**
 * Dialog service for managing application-wide modal dialogs.
 * Uses Angular signals for reactive state management.
 */

import { Injectable, signal } from '@angular/core';

/**
 * Configuration interface for dialog content.
 *
 */
export interface DialogConfig {
  /** @remarks Main headline text displayed in the dialog */
  headline: string;
  /** @remarks Body text content of the dialog */
  text: string;
}

/**
 * Service for managing application-wide modal dialogs.
 *
 * @remarks Provides centralized dialog state management using Angular signals.
 * Allows opening and closing dialogs with configurable content.
 * Uses reactive signals for efficient change detection and component updates.
 *
 */
@Injectable({
  providedIn: 'root',
})
export class DialogService {
  /** @private Internal signal for dialog visibility state */
  private readonly _isVisible = signal(false);
  /** @private Internal signal for dialog configuration */
  private readonly _config = signal<DialogConfig>({ headline: '', text: '' });

  /** @readonly Public readonly signal for dialog visibility state */
  readonly isVisible = this._isVisible.asReadonly();
  /** @readonly Public readonly signal for dialog configuration */
  readonly config = this._config.asReadonly();

  /**
   * Opens a dialog with the specified configuration.
   *
   * @remarks Sets the dialog content and makes it visible.
   * The dialog will be displayed with the provided headline and text.
   *
   * @param {DialogConfig} config - Configuration object containing dialog content
   */
  openDialog(config: DialogConfig): void {
    this._config.set(config);
    this._isVisible.set(true);
  }

  /**
   * Closes the currently open dialog.
   *
   * @remarks Hides the dialog by setting visibility to false.
   * The dialog configuration is preserved until a new dialog is opened.
   *
   */
  closeDialog(): void {
    this._isVisible.set(false);
  }
}
